import { createJSONStorage, createStringStorage } from "@/storage/browser"

const MAX_PENDING_PUSH_DETACHES = 16

export type PendingPushDetach = {
  owner: string
  endpoint: string
}

const bindingTokenStorage = createStringStorage("mux.push.binding.v1")
const registrationFingerprintStorage = createStringStorage("mux.push.registration.v1")
const registrationVerifiedAtStorage = createStringStorage("mux.push.registration-verified-at.v1")
const pendingDetachStorage = createJSONStorage<PendingPushDetach[]>("mux.push.pending-detach.v2", {
  decode(value) {
    if (!Array.isArray(value)) return []
    const records: PendingPushDetach[] = []
    for (const item of value) {
      if (!item || typeof item !== "object" || Array.isArray(item)) continue
      const record = item as Record<string, unknown>
      if (typeof record.owner !== "string" || !record.owner) continue
      if (typeof record.endpoint !== "string" || !record.endpoint) continue
      records.push({ owner: record.owner, endpoint: record.endpoint })
    }
    return records.slice(-MAX_PENDING_PUSH_DETACHES)
  },
})
const pushEnabledStorage = createJSONStorage<boolean | undefined>("mux.push.enabled.v1", {
  decode(value) {
    return typeof value === "boolean" ? value : undefined
  },
})

export function readPushEnabledPreference() {
  return pushEnabledStorage.read(undefined)
}

export function writePushEnabledPreference(value: boolean) {
  pushEnabledStorage.write(value)
}

export function readPushBindingToken() {
  return bindingTokenStorage.read()
}

export function writePushBindingToken(value: string) {
  bindingTokenStorage.write(value)
  if (!value) {
    registrationFingerprintStorage.write("")
    registrationVerifiedAtStorage.write("")
  }
}

export function readPushRegistrationFingerprint() {
  return registrationFingerprintStorage.read()
}

export function writePushRegistrationFingerprint(value: string) {
  registrationFingerprintStorage.write(value)
  registrationVerifiedAtStorage.write(String(Date.now()))
}

export function pushRegistrationIsFresh(maxAgeMs: number) {
  const verifiedAt = Number(registrationVerifiedAtStorage.read())
  return Number.isFinite(verifiedAt) && Date.now() - verifiedAt < maxAgeMs
}

export function readPendingPushDetaches(owner: string) {
  if (!owner) return []
  return pendingDetachStorage.read([]).filter((record) => record.owner === owner)
}

export function queuePendingPushDetach(owner: string, endpoint: string) {
  if (!owner || !endpoint) return
  const records = pendingDetachStorage.read([])
  if (records.some((record) => record.owner === owner && record.endpoint === endpoint)) return
  pendingDetachStorage.write([...records, { owner, endpoint }].slice(-MAX_PENDING_PUSH_DETACHES))
}

export function clearPendingPushDetach(owner: string, endpoint: string) {
  if (!owner || !endpoint) return
  const records = pendingDetachStorage.read([])
  pendingDetachStorage.write(records.filter((record) => record.owner !== owner || record.endpoint !== endpoint))
}

export async function pushServiceWorkerRegistration() {
  await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" })
  return navigator.serviceWorker.ready
}

export async function currentPushSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null
  const registration = await pushServiceWorkerRegistration()
  return registration.pushManager.getSubscription()
}

export async function unsubscribePushDeviceLocally(subscription?: PushSubscription | null, owner = "") {
  const current = subscription ?? (await currentPushSubscription())
  if (current && owner) queuePendingPushDetach(owner, current.endpoint)
  if (current) await current.unsubscribe().catch(() => false)
  writePushBindingToken("")
}
